import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { SlidersHorizontal } from "lucide-react";

import { CompactHero } from "@/components/brand/CompactHero";
import { DecisionItems } from "@/components/projects/calibration/DecisionItems";
import { LiveResult } from "@/components/projects/calibration/LiveResult";
import { ParameterPanel } from "@/components/projects/calibration/ParameterPanel";
import { ScopeDriftNotice } from "@/components/projects/calibration/ScopeDriftNotice";
import { ScopeItemPicker } from "@/components/projects/calibration/ScopeItemPicker";
import { WricefExtensionSuggestion } from "@/components/projects/calibration/WricefExtensionSuggestion";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  useCalibration,
  useCalibrationParameters,
  useCalibrationPreview,
  useCalibrationScopeItems,
  useOpenCalibration,
  usePublishCalibration,
} from "@/hooks/useCalibration";
import { useProject } from "@/hooks/useApi";
import { useWricefDecisionSummary } from "@/hooks/useWricefDecisions";
import { formatDateTime } from "@/lib/datetime";

/** O código estável que a rota põe ao lado da mensagem do 409.
 *
 * O `_conflict` do servidor declara que "a tela decide pelo código", e a
 * primeira versão deste ramo ignorava o código e dizia sempre "confira os
 * parâmetros" — inclusive para quem tinha um item de escopo que saiu do
 * catálogo, e que não tem o que consertar em parâmetro nenhum.
 */
function previewErrorCode(error: unknown): string | null {
  const detail = (
    error as { response?: { data?: { detail?: { code?: string } } } }
  )?.response?.data?.detail;
  return detail?.code ?? null;
}

/** O parâmetro de dias de referência de cada banda, como o motor os chama. */
const REFERENCE_DAYS_KEYS = { low: "refBaixa", medium: "refMedia", high: "refAlta" } as const;

/** Espera antes de recalcular, para digitar num campo não disparar uma chamada por tecla. */
const DEBOUNCE_MS = 250;

/** O que a publicação congelou em `calibrated` (no servidor, jsonb livre). */
type PublishedBody = {
  reclassified?: Record<string, string>;
  param_overrides?: Record<string, number>;
  agent_opt_out?: string[];
  numbers?: { avg_cost_per_hour?: number };
};

/**
 * Os parâmetros de que a tela parte, a partir da versão publicada.
 *
 * Até a EF-FUNC-012 o `tarifaHH` era a tarifa blended de venda; desde então é o
 * custo médio da equipe. A publicação daquela época não traz `avg_cost_per_hour`
 * nos números, e o `tarifaHH` dela é preço: semeá-lo poria o preço no campo de
 * custo médio, e a Calibragem comercial calcularia margem sobre ele. Sem ele, o
 * campo parte do padrão do catálogo.
 */
function publishedOverrides(published: PublishedBody): Record<string, number> {
  const overrides = { ...(published.param_overrides ?? {}) };
  if (published.numbers?.avg_cost_per_hour == null) delete overrides.tarifaHH;
  return overrides;
}

/**
 * Tela de Calibragem (#877, S-079).
 *
 * A etapa humana entre os agentes e o Diagnóstico: aqui o consultor
 * reclassifica o que discorda, ajusta os parâmetros e vê o número mudar.
 * Publicar congela o que ele entregou.
 *
 * ## A lista é o Fit-to-Standard
 *
 * Os itens são os Ativar da aprovação vigente, e o servidor monta a seleção
 * (22/10/2026). Item que falta se ativa no Functional: somá-lo só aqui deixaria
 * o número fora do escopo que os outros agentes leem. Os itens em decisão
 * aparecem à parte, sem horas.
 *
 * ## Nada aqui grava até publicar
 *
 * O recálculo é uma chamada que não escreve. É o que sustenta o critério "sair
 * sem publicar não altera o que o Diagnóstico mostra" — sair desta tela no meio
 * não deixa rastro nenhum.
 *
 * ## A memória de cálculo mora nesta tela
 *
 * O Ato 5 mostra o resultado ao cliente; a conta passo a passo fica de deste
 * lado, que é o de quem trabalha o número.
 */
export default function ProjectCalibration() {
  const { projectId } = useParams<{ projectId: string }>();
  const { t } = useTranslation();

  const { data: project } = useProject(projectId);
  const { data: scope } = useCalibrationScopeItems(projectId);
  const { data: catalogParameters } = useCalibrationParameters(projectId);
  //: EF-FUNC-012 RF-02: o `tarifaHH` é o custo médio da equipe por hora, e não
  //: mais a tarifa blended de venda. O rótulo do catálogo é texto do banco, sem
  //: tradução, e ficava com o nome antigo até o seed 1.3 rodar; a tela usa o seu.
  const parameters = useMemo(
    () =>
      catalogParameters?.map((parameter) =>
        parameter.param_key === "tarifaHH"
          ? { ...parameter, label: t("Average team cost / HH") }
          : parameter,
      ),
    [catalogParameters, t],
  );
  const { data: calibration, isPending: calibrationPending } =
    useCalibration(projectId);
  const openCalibration = useOpenCalibration(projectId);
  const publish = usePublishCalibration(projectId);
  const preview = useCalibrationPreview(projectId);

  const [reclassified, setReclassified] = useState<Record<string, string>>({});
  const [overrides, setOverrides] = useState<Record<string, number>>({});
  //: A tela não edita o opt-out por agente, mas republicar não pode apagá-lo.
  const [agentOptOut, setAgentOptOut] = useState<string[]>([]);
  //: Ponto de partida: com versão publicada, as escolhas dela (banda,
  //: parâmetros e opt-out), senão republicar apaga o que ela tinha. A seleção
  //: não está aqui: ela sai da aprovação, no servidor.
  //:
  //: Uma vez só, quando a calibragem chega: depois disso o estado é da pessoa,
  //: e reaplicar o ponto de partida apagaria o que ela mexeu.
  const [seeded, setSeeded] = useState(false);

  useEffect(() => {
    if (seeded || calibrationPending) return;
    const published = calibration?.calibrated as PublishedBody | null | undefined;
    if (published) {
      setReclassified(published.reclassified ?? {});
      setOverrides(publishedOverrides(published));
      setAgentOptOut(published.agent_opt_out ?? []);
    }
    setSeeded(true);
  }, [seeded, calibration, calibrationPending]);

  //: Sem escopo aprovado não há lista, e o servidor recusa recalcular.
  const approved = scope?.approval_version != null;
  //: O que entra na lista ao publicar de novo vem marcado, com a banda do
  //: catálogo, para revisar antes.
  const entered = useMemo(() => new Set(calibration?.scope_entered ?? []), [calibration]);
  const decisionCount = scope?.decision?.length ?? 0;
  //: Os dias de referência de cada banda, com o ajuste da tela. É por eles que
  //: o item sem curadoria conta, e a lista mostra o mesmo número que o motor usa.
  const referenceDays = useMemo(() => {
    const value = (key: string) =>
      overrides[key] ?? parameters?.find((p) => p.param_key === key)?.default_value;
    const days: Record<string, number> = {};
    for (const [band, key] of Object.entries(REFERENCE_DAYS_KEYS)) {
      const v = value(key);
      if (v != null) days[band] = v;
    }
    return days;
  }, [overrides, parameters]);

  const reclassify = useCallback((code: string, complexity: string) => {
    setReclassified((current) => ({ ...current, [code]: complexity }));
  }, []);

  const changeParameter = useCallback((key: string, value: number) => {
    setOverrides((current) => ({ ...current, [key]: value }));
  }, []);

  //: A sugestão do Catálogo WRICEF para as extensões (#1712, cartão 3.1): entra
  //: nos parâmetros só no clique, como qualquer correção da tela.
  const { data: wricefSummary } = useWricefDecisionSummary(projectId);
  const parameterValues = useMemo(() => {
    const values: Record<string, number> = {};
    for (const p of parameters ?? []) values[p.param_key] = p.default_value;
    return { ...values, ...overrides };
  }, [parameters, overrides]);
  const applySuggestion = useCallback((values: Record<string, number>) => {
    setOverrides((current) => ({ ...current, ...values }));
  }, []);

  const body = useMemo(
    () => ({
      reclassified,
      param_overrides: overrides,
      agent_opt_out: agentOptOut,
    }),
    [reclassified, overrides, agentOptOut],
  );

  const { mutate: recalculate } = preview;
  //: Para QUAL corpo o `numbers` foi calculado. Sem isso não dá para saber se
  //: o que está na tela corresponde ao que está selecionado agora: entre a
  //: última edição e o disparo do timer existe uma janela de até 250ms em que
  //: `preview.isPending` é falso e `numbers` é o resultado ANTERIOR.
  const [computedFor, setComputedFor] = useState<typeof body | null>(null);
  useEffect(() => {
    if (!approved) return;
    const timer = setTimeout(
      () => recalculate(body, { onSuccess: () => setComputedFor(body) }),
      DEBOUNCE_MS,
    );
    return () => clearTimeout(timer);
  }, [body, recalculate, approved]);

  const numbers = preview.data;
  //: Cobre a janela do debounce E o voo da requisição, e por isso substitui o
  //: `preview.isPending`, que sozinho deixava a primeira passar.
  const stale = computedFor !== body;

  //: #877: o que vai ser publicado, congelado no instante em que a pergunta
  //: abre. O recálculo tem debounce de 250ms e NÃO para com a modal aberta:
  //: um timer armado antes do clique dispara depois dele, e o `mutate` do
  //: TanStack zera o `data` enquanto refaz a conta. Os números o servidor
  //: refaz na publicação; os daqui servem ao aviso de custo médio da pergunta.
  const [pendingPublish, setPendingPublish] = useState<
    (typeof body & { numbers: typeof preview.data }) | null
  >(null);

  return (
    <div className="flex flex-col gap-6">
      <CompactHero
        glyph={<SlidersHorizontal className="h-5 w-5" />}
        overline={project?.name ?? ""}
        title={t("Calibration")}
        meta={
          // Enquanto a consulta não volta, não se sabe se há calibragem: dizer
          // "nenhuma aberta" punha o "Abrir calibragem" à mão, e em produção a
          // consulta passou de 4 s. Publicada, o topo diz quando e por quem,
          // porque republicar a mesma versão não muda o número dela.
          calibrationPending
            ? t("Loading the calibration…")
            : calibration
              ? calibration.status === "published" && calibration.published_at
                ? t("Version {{version}} · published on {{when}} by {{who}}", {
                    version: calibration.version,
                    when: formatDateTime(calibration.published_at),
                    who: calibration.published_by_name ?? "—",
                  })
                : t("Version {{version}} · {{status}}", {
                    version: calibration.version,
                    status: t(calibration.status),
                  })
              : t("No calibration open")
        }
        actions={
          calibrationPending ? null : calibration ? (
            <AlertDialog
              open={pendingPublish !== null}
              onOpenChange={(aberto) =>
                setPendingPublish(
                  //: As escolhas INTEIRAS: a reclassificação achata
                  //: dias-base e os overrides mexem nos 55 parâmetros.
                  //: Congelar metade guarda um registro que não reproduz o
                  //: próprio número, e é esse registro que o laço de
                  //: aprendizado (#878) compara campo a campo.
                  aberto && numbers ? { ...body, numbers } : null,
                )
              }
            >
              <AlertDialogTrigger asChild>
                {/* `stale` e não `preview.isPending`: o `isPending` sozinho
                    deixava passar a janela do debounce, que é de até 250ms
                    entre a última edição e o disparo do recálculo. Nela o
                    `numbers` é o resultado ANTERIOR, e publicar congelaria um
                    escopo que não produziu aqueles números. */}
                <Button disabled={publish.isPending || stale || !numbers || !approved}>
                  {t("Publish")}
                </Button>
              </AlertDialogTrigger>
              {/* Publicar não é salvar: congela o que a pessoa entregou e troca
                  o que o CLIENTE vê no Diagnóstico. Mesma família de
                  consequência da aprovação de escopo do Functional, e por isso
                  a mesma forma de perguntar. */}
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>
                    {t("Publish this calibration?")}
                  </AlertDialogTitle>
                  <AlertDialogDescription>
                    {t(
                      "From here on the Diagnostic shows this version to the client, instead of the latest run. Re-running an agent no longer changes what was presented.",
                    )}
                  </AlertDialogDescription>
                </AlertDialogHeader>
                {/* Sem custo médio o custo do projeto dá zero, e a Calibragem
                    comercial não tem sobre o que calcular os cenários. Avisa e
                    deixa publicar, porque prazo e horas valem sem custo. */}
                {/* Os itens em decisão estão no escopo e fora das horas. Avisa e
                    deixa publicar: o número cresce quando o Functional decidir
                    e a Calibragem for publicada de novo. */}
                {pendingPublish !== null && decisionCount > 0 && (
                  <p
                    data-testid="publish-with-decision"
                    className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800"
                  >
                    {t(
                      "{{count}} items awaiting a decision stay out of the hours. To count them, decide them in the Functional, approve the scope and publish the calibration again.",
                      { count: decisionCount },
                    )}
                  </p>
                )}
                {pendingPublish !== null &&
                  !((pendingPublish.numbers?.project_cost ?? 0) > 0) && (
                    <p
                      data-testid="publish-without-rate"
                      className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800"
                    >
                      {t(
                        "Without the average team cost per hour the project cost is zero, and the Commercial calibration has nothing to price the scenarios on. Fill in the average cost to use it there.",
                      )}
                    </p>
                  )}
                <AlertDialogFooter>
                  <AlertDialogCancel>{t("Cancel")}</AlertDialogCancel>
                  {/* O `disabled` do gatilho não vale aqui: ele fica atrás da
                      modal. E o Radix mantém o nó montado durante a animação
                      de saída, então dois Enter seguidos disparariam dois
                      `mutate` — o segundo acha a calibragem já publicada e
                      devolve 409 numa operação que deu certo. */}
                  <AlertDialogAction
                    disabled={publish.isPending || pendingPublish === null}
                    onClick={() => {
                      if (!pendingPublish) return;
                      const { numbers: _shown, ...choices } = pendingPublish;
                      publish.mutate(choices);
                    }}
                  >
                    {t("Publish")}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          ) : (
            <Button
              onClick={() => openCalibration.mutate()}
              disabled={openCalibration.isPending}
            >
              {t("Open calibration")}
            </Button>
          )
        }
      />

      {scope ? <ScopeDriftNotice calibration={calibration} scope={scope} /> : null}

      {openCalibration.isError ? (
        <Card>
          <CardContent className="p-4 text-sm text-destructive">
            {t(
              "Could not open the calibration. The agents may not have finished yet.",
            )}
          </CardContent>
        </Card>
      ) : null}

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-[minmax(0,1fr)_minmax(0,28rem)]">
        <Tabs defaultValue="scope">
          <TabsList>
            <TabsTrigger value="scope">{t("Scope items")}</TabsTrigger>
            <TabsTrigger value="parameters">{t("Parameters")}</TabsTrigger>
          </TabsList>
          <TabsContent value="scope" className="pt-4">
            <Card>
              <CardContent className="flex flex-col gap-4 p-4">
                <ScopeItemPicker
                  items={scope?.items ?? []}
                  approvalVersion={scope?.approval_version}
                  entered={entered}
                  referenceDays={referenceDays}
                  reclassified={reclassified}
                  onReclassify={reclassify}
                />
                <DecisionItems items={scope?.decision ?? []} />
              </CardContent>
            </Card>
          </TabsContent>
          <TabsContent value="parameters" className="pt-4">
            <Card>
              <CardContent className="p-4">
                <ParameterPanel
                  parameters={parameters ?? []}
                  overrides={overrides}
                  onChange={changeParameter}
                  extras={{
                    commercial: (
                      <p
                        data-testid="average-cost-help"
                        className="rounded-md border border-[#e8ebf0] bg-[#f7f9fc] px-3 py-2 text-xs text-[#596b85]"
                      >
                        {t(
                          "Average team cost per hour, not a sale price. Taxes and the target margin are applied per scenario in the Commercial calibration.",
                        )}
                      </p>
                    ),
                    extension: (
                      <WricefExtensionSuggestion
                        summary={wricefSummary}
                        values={parameterValues}
                        onApply={applySuggestion}
                      />
                    ),
                  }}
                />
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        <Card className="h-fit">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">{t("Live estimate")}</CardTitle>
          </CardHeader>
          <CardContent>
            {/* O recálculo pode falhar, e sem este ramo o `mutate` zera o
                `data`, o `LiveResult` cai no caminho de "nenhum resultado" e
                diz que não há estimativa com a lista cheia. O botão de
                publicar já desabilita sozinho; o que faltava era dizer por
                quê.

                A mensagem vem do CÓDIGO do erro, e não é única: a rota
                devolve dois 409 diferentes (`parameter_out_of_range` e
                `unknown_scope_item`), e o docstring do `_conflict` diz que
                "a tela decide pelo código". Mandar quem tomou
                `unknown_scope_item` conferir parâmetros é mandá-lo para uma
                aba onde não há nada a consertar.

                A borda de faixa é do SERVIDOR, e não do `type="number"`: um
                `min` no input só valida dentro de `<form>` com submit, e não
                é o caso aqui. Tentar sugerir a faixa exigiria expor `min`/
                `max` no `ParameterOut`, que é contrato e escopo próprio. */}
            {preview.isError ? (
              <p
                data-testid="calibration-preview-error"
                className="rounded-md border border-[#e8c4c4] bg-[#fdf4f4] p-3 text-sm text-[#8a3a3a]"
              >
                {previewErrorCode(preview.error) === "unknown_scope_item"
                  ? t(
                      "The estimate could not be recalculated — a selected scope item no longer exists in the active catalogue.",
                    )
                  : previewErrorCode(preview.error) === "parameter_out_of_range"
                    ? t(
                        "The estimate could not be recalculated — one of the parameters is out of range.",
                      )
                    : t("The estimate could not be recalculated.")}
              </p>
            ) : (
              <LiveResult preview={numbers} pending={preview.isPending} />
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
